import { useSyncExternalStore } from 'react'
import arabic from '@/locales/ar.json'

export type Language = 'en' | 'ar'
export const LANGUAGE_STORAGE_KEY = 'ielts.interface-language'
const listeners = new Set<() => void>()
const catalogue = arabic as Record<string, string>

function savedLanguage(): Language {
  try { return localStorage.getItem(LANGUAGE_STORAGE_KEY) === 'ar' ? 'ar' : 'en' } catch { return 'en' }
}
let language: Language = savedLanguage()

export function setLanguage(next: Language) {
  if (next !== 'en' && next !== 'ar') return
  language = next
  try { localStorage.setItem(LANGUAGE_STORAGE_KEY, next) } catch { /* The current tab still works with storage disabled. */ }
  listeners.forEach((notify) => notify())
}

export function currentLanguage(): Language {
  return language
}

function subscribe(notify: () => void) {
  listeners.add(notify)
  return () => { listeners.delete(notify) }
}

/** Subscribe without remounting the page or resetting an in-progress form. */
export function useLanguage() {
  return useSyncExternalStore(subscribe, () => language, () => 'en' as const)
}

type MissingTranslationHandler = (source: string) => void
let missingTranslation: MissingTranslationHandler | null = null

/**
 * Test-only hook: called with every source string that has no Arabic entry
 * while Arabic is active. Production never registers a handler, so a miss
 * still falls back to the English source silently for students.
 */
export function onMissingTranslation(handler: MissingTranslationHandler | null) {
  missingTranslation = handler
}

/** True when the catalogue has Arabic for this exact source (whitespace-insensitive). */
export function hasTranslation(source: string) {
  const key = source.trim()
  return key !== '' && Object.hasOwn(catalogue, key)
}

function lookup(key: string): string | undefined {
  return key && Object.hasOwn(catalogue, key) ? catalogue[key] : undefined
}

/**
 * Only source-owned interface copy belongs here, never exam or user content.
 *
 * Numeric values are shown with Arabic-Indic digits in Arabic (owner decision,
 * 2026-09-25). Pass identifiers, codes and anything a student must copy
 * exactly as strings: string values are inserted untouched.
 */
export function t(source: string, values: Record<string, unknown> = {}): string {
  let message = source
  if (language === 'ar') {
    const key = source.trim()
    const translated = lookup(key)
    if (translated) message = source.replace(key, () => translated)
    else if (key && missingTranslation) missingTranslation(key)
  }
  return fill(message, values)
}

function fill(message: string, values: Record<string, unknown>) {
  return message.replace(/\{(\d+)\}/g, (placeholder, key: string) => {
    if (!Object.hasOwn(values, key)) return placeholder
    const value = values[key]
    return typeof value === 'number' ? localizeDigits(String(value)) : String(value)
  })
}

/**
 * A count phrase with the correct Arabic plural form.
 *
 * English picks `one` for exactly 1 and `other` otherwise, exactly as the
 * existing `${n} thing${s}` copy did. Arabic looks up the `other` source with a
 * CLDR plural category suffix (`#zero`, `#one`, `#two`, `#few`, `#many`) and
 * falls back to the plain `other` entry, which holds the form used after 11+.
 */
export function tCount(count: number, one: string, other: string, values: Record<string, unknown> = {}): string {
  const merged = { 0: count, ...values }
  if (language !== 'ar') return t(count === 1 ? one : other, merged)
  const key = other.trim()
  const category = new Intl.PluralRules('ar').select(count)
  const translated = lookup(`${key}#${category}`) ?? lookup(key)
  if (!translated) {
    if (key && missingTranslation) missingTranslation(key)
    return fill(count === 1 ? one : other, merged)
  }
  return fill(other.replace(key, () => translated), merged)
}

/**
 * Interface copy whose English word has more than one Arabic sense ("Open" as
 * a button versus a ticket status). Arabic looks up `source#context` first and
 * falls back to the plain entry; English is unchanged.
 */
export function tIn(context: string, source: string, values: Record<string, unknown> = {}): string {
  if (language !== 'ar') return t(source, values)
  const key = source.trim()
  const translated = lookup(`${key}#${context}`)
  return translated ? fill(source.replace(key, () => translated), values) : t(source, values)
}

/** Kept for existing admin copy; new student copy should use `tCount`. */
export function englishPluralSuffix(count: number | null | undefined) {
  return language === 'en' && count !== 1 ? 's' : ''
}

const ARABIC_ZERO = 0x0660

/**
 * Convert an interface number that is already formatted the English way into
 * Arabic-Indic digits, with the Arabic decimal (U+066B), thousands (U+066C)
 * and percent (U+066A) signs. English text is returned unchanged, so English
 * output stays byte-identical.
 *
 * Never use this for phone numbers, e-mail addresses, codes, IDs, URLs,
 * typed values or exam content: those stay Latin by owner decision.
 */
export function localizeDigits(text: string): string {
  if (language !== 'ar') return text
  return text
    .replace(/(\d)\.(?=\d)/g, '$1\u066B')
    .replace(/(\d),(?=\d{3}(?!\d))/g, '$1\u066C')
    .replace(/(\d)%/g, '$1\u066A')
    .replace(/\d/g, (digit) => String.fromCharCode(ARABIC_ZERO + Number(digit)))
}

/**
 * Digits a student typed, as ASCII. An Arabic keyboard types Arabic-Indic
 * (U+0660-0669) or Persian (U+06F0-06F9) digits; values are sent and stored
 * with ASCII digits. Everything else is left as typed.
 */
export function toAsciiDigits(text: string): string {
  return text
    .replace(/[\u0660-\u0669]/g, (digit) => String(digit.charCodeAt(0) - ARABIC_ZERO))
    .replace(/[\u06F0-\u06F9]/g, (digit) => String(digit.charCodeAt(0) - 0x06F0))
}

/** Locale for Intl number formatting of interface numbers. */
export function numberLocale() {
  return language === 'ar' ? 'ar-u-nu-arab' : 'en-GB'
}

/** Gregorian calendar in both languages; Arabic-Indic digits in Arabic. */
export function dateLocale() {
  return language === 'ar' ? 'ar-u-ca-gregory-nu-arab' : 'en-GB'
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions) {
  return new Intl.NumberFormat(numberLocale(), options).format(value)
}

/** An IELTS band always shows one decimal: 6.0, 6.5. */
export function formatBand(value: number) {
  return localizeDigits(value.toFixed(1))
}

/** `value` is already on a 0-100 scale and already rounded by the caller. */
export function formatPercent(value: number) {
  if (language !== 'ar') return `${value}%`
  return new Intl.NumberFormat(numberLocale(), { style: 'percent', maximumFractionDigits: 2 }).format(value / 100)
}

/**
 * A score or a count out of a total: "3 / 15" in English. Arabic says "3 of
 * 15" in words, because a slash between two numbers is read in the other
 * order in right-to-left text.
 */
export function formatFraction(part: number | string, whole: number | string) {
  return t('{0} / {1}', { 0: localizeDigits(String(part)), 1: localizeDigits(String(whole)) })
}

/** Minutes and seconds, `m:ss`. */
export function formatClock(totalSeconds: number) {
  const whole = Math.max(0, Math.round(totalSeconds))
  const minutes = Math.floor(whole / 60)
  const seconds = whole % 60
  return localizeDigits(`${minutes}:${String(seconds).padStart(2, '0')}`)
}

/** Compact elapsed time: `1h 21m` / `57m`, or the Arabic equivalent. */
export function formatHoursMinutes(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60)
  return hours ? t('{0}h {1}m', { 0: hours, 1: totalMinutes % 60 }) : t('{0}m', { 0: totalMinutes })
}

/**
 * A plain list. English keeps its existing join - commas, or "a and b" when
 * `style` is 'and'; Arabic always uses its own conjunction.
 */
export function formatList(items: readonly string[], style: 'comma' | 'and' = 'comma') {
  if (language === 'ar') return new Intl.ListFormat('ar', { type: 'conjunction' }).format(items)
  return style === 'and' ? new Intl.ListFormat('en-GB', { type: 'conjunction' }).format(items) : items.join(', ')
}

/** A month name for the interface language, from a 1-12 month number. */
export function formatMonthName(month: number) {
  return new Intl.DateTimeFormat(dateLocale(), { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, month - 1, 1)))
}

/** An arrow pointing the way the interface reads: right in English, left in Arabic. */
export function forwardArrow() {
  return language === 'ar' ? '\u2190' : '\u2192'
}

/** The comma or semicolon (plus space) that joins two interface phrases. */
export function joiner(mark: ',' | ';') {
  if (language !== 'ar') return `${mark} `
  return mark === ',' ? '\u060C ' : '\u061B '
}

/**
 * Wrap a value that must stay Latin and left-to-right (a phone number, an
 * e-mail address, a reference) so an Arabic sentence cannot reorder it.
 * For attribute and plain-text use; in JSX prefer the `Ltr` component.
 */
export function isolateLtr(text: string) {
  return language === 'ar' ? `\u2066${text}\u2069` : text
}

/**
 * Wrap authored or user content (a test title, a name) placed inside Arabic
 * attribute text, so its own direction is kept without reordering the Arabic.
 */
export function isolateContent(text: string) {
  return language === 'ar' ? `\u2068${text}\u2069` : text
}

export function isEnglishTestRoute(pathname: string) {
  return /^\/test\/[^/]+\/?$/i.test(pathname) || /^\/admin\/tests\/[^/]+\/preview\/?$/i.test(pathname)
}
